/* Public Gallery page.
 *
 * Layout:
 *   • Top: <SectionMedia sectionKey="events-gallery" /> banner
 *   • Below: one section per Media Library album with photos.
 *     The album name renders as the event title above its photo grid.
 *
 * Admins manage everything from the existing Media Library — creating
 * an album + uploading photos into it is all it takes for a new event
 * group to appear here.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Image as ImageIcon } from 'lucide-react';
import SectionMedia from '../components/SectionMedia';
import ImageLightbox from '../components/ImageLightbox';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SERVE = `${BACKEND_URL}/api/media/serve`;

const buildSrcSet = (photo) => {
  // Native Media Library items have variants (mobile/tablet/desktop).
  // Published references (from Event QR / Events / Distributions /
  // Community Updates) render off a single display_url — no srcSet.
  const V = photo.variants || {};
  if (!Object.keys(V).length) return undefined;
  const parts = [];
  if (V.mobile) parts.push(`${SERVE}/${photo.id}/mobile ${V.mobile.width}w`);
  if (V.tablet) parts.push(`${SERVE}/${photo.id}/tablet ${V.tablet.width}w`);
  if (V.desktop) parts.push(`${SERVE}/${photo.id}/desktop ${V.desktop.width}w`);
  return parts.join(', ') || undefined;
};

const PhotoTile = ({ photo, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(photo)}
    className="group relative aspect-square overflow-hidden rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#BA1826]"
    data-testid={`gallery-photo-${photo.id}`}
    aria-label={photo.alt_text || photo.title || 'Open photo'}
  >
    <img
      src={photo.display_url || `${SERVE}/${photo.id}/tablet`}
      srcSet={buildSrcSet(photo)}
      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 300px"
      alt={photo.alt_text || photo.title || ''}
      loading="lazy"
      decoding="async"
      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
    />
    {photo.title && (
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-2 text-left opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <span className="text-xs font-medium text-white line-clamp-2">
          {photo.title}
        </span>
      </div>
    )}
  </button>
);

const AlbumGroup = ({ album, onOpenPhoto }) => (
  <section
    className="mb-14"
    data-testid={`gallery-album-${album.slug}`}
    aria-labelledby={`gallery-album-title-${album.slug}`}
  >
    <div className="mb-4 border-l-4 border-[#BA1826] pl-3">
      <h2
        id={`gallery-album-title-${album.slug}`}
        className="font-heading text-2xl md:text-3xl font-bold text-[#0A0A0A]"
        data-testid={`gallery-album-title-${album.slug}`}
      >
        {album.name}
      </h2>
      {album.description && (
        <p className="mt-1 text-sm text-gray-600">{album.description}</p>
      )}
      <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">
        {album.photo_count} {album.photo_count === 1 ? 'photo' : 'photos'}
      </p>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {album.photos.map((p) => (
        <PhotoTile key={p.id} photo={p} onOpen={onOpenPhoto} />
      ))}
    </div>
  </section>
);

const Gallery = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND_URL}/api/public/media/gallery`)
      .then((r) => {
        if (!alive) return;
        setAlbums(r.data?.albums || []);
        setLoading(false);
      })
      .catch((e) => {
        logger.error('Gallery load failed:', e);
        if (!alive) return;
        setError('We couldn\'t load the gallery right now. Please refresh in a moment.');
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="gallery-page">
      {/* Hero banner — admins configure via Site Sections → events-gallery */}
      <section className="relative h-64 md:h-80 overflow-hidden bg-[#0A0A0A]">
        <SectionMedia
          sectionKey="events-gallery"
          className="absolute inset-0 h-full w-full"
          objectFit="cover"
          priority
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex h-full items-center justify-center px-6 text-center">
          <div>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white"
              data-testid="gallery-page-title"
            >
              Gallery
            </h1>
            <p className="mt-3 text-sm md:text-base text-white/80 max-w-lg mx-auto">
              Moments from our events, outreach programs, and volunteer days.
            </p>
          </div>
        </div>
      </section>

      {/* Album groups */}
      <div className="px-6 py-12">
        {loading && (
          <div className="py-16 text-center text-gray-500" data-testid="gallery-loading">
            Loading photos…
          </div>
        )}

        {!loading && error && (
          <div className="py-16 text-center text-gray-600" data-testid="gallery-error">
            {error}
          </div>
        )}

        {!loading && !error && albums.length === 0 && (
          <div
            className="py-20 text-center text-gray-500"
            data-testid="gallery-empty"
          >
            <ImageIcon className="mx-auto mb-3 h-10 w-10 text-gray-300" />
            <p className="font-medium">Photos coming soon.</p>
            <p className="mt-1 text-sm">
              We&apos;re getting ready to share moments from our recent events.
            </p>
          </div>
        )}

        {!loading && !error && albums.map((album) => (
          <AlbumGroup
            key={album.id}
            album={album}
            onOpenPhoto={setLightboxPhoto}
          />
        ))}
      </div>

      {lightboxPhoto && (
        <ImageLightbox
          src={lightboxPhoto.full_url || `${SERVE}/${lightboxPhoto.id}/desktop`}
          alt={lightboxPhoto.alt_text || lightboxPhoto.title || ''}
          onClose={() => setLightboxPhoto(null)}
        />
      )}
    </div>
  );
};

export default Gallery;
